import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { LoginFlow } from "./components";
import { RootState } from "src/store";
import { authorizationGateActions } from "./authorization-gate-store";
import { connect } from "react-redux";
import { hasValue, hasValueAndNotEmpty, Nullable } from "@itk/typescript-utils";
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useTheme } from "@mui/material";
import { emailKey, tokenKey } from "./authorization-gate-utils";
import { Token } from "./authorization-gate-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { LoginService } from "src/backend/openapi/v1";
import dekoLogoAndText from "../../assets/images/deko-logo-and-text.svg";
import authBackgroundImage from "../../assets/images/auth-background-image.svg";
import sidebarBackground from "../../assets/images/sidebar-background.svg";
import { CONSTANTS, ROUTES_URL } from "src/utils/constants";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useLoginPageT } from "src/translation-hooks/login-page";
import { useResetPasswordPageT } from "src/translation-hooks/reset-password-page";
import { useProfileInfoPageT } from "src/translation-hooks/profile-info-page";
import { useNewPasswordPageT } from "src/translation-hooks/new-password-page";
import { useLoginWithOtpPageT } from "src/translation-hooks/login-with-otp-page";
import { LoginFlowFields } from "./components/login-flow/login-flow-types";
import { email, equal, handleOnFieldChange, OptionalFormController, OptionalFormInclude, required, useForm } from "@itk/react-form";
import { DekoxIcon } from "../deko-x-icons";

const mapStateToProps = (state: RootState) => ({ ...state.generalReducer.authorizationGateReducer });

const ownActions = {
  validateUserRequest: authorizationGateActions.validateUserRequest,
  validateOtpRequest: authorizationGateActions.validateOtpRequest,
  changePasswordRequest: authorizationGateActions.changePasswordRequest,
  setPasswordRequest: authorizationGateActions.setPasswordRequest,
  getLoginRequest: authorizationGateActions.getLoginRequest,
  renewAccessTokenRequest: authorizationGateActions.renewAccessTokenRequest,
  setLoginState: authorizationGateActions.setLoginState,
  setLoginFlowState: authorizationGateActions.setLoginFlowState,
  setRememberMe: authorizationGateActions.setRememberMe,
  setForgotPassword: authorizationGateActions.setForgotPassword,
  logoutRequest: authorizationGateActions.logoutRequest,
  setEmail: authorizationGateActions.setEmail,
  alternativeLoginRequest: authorizationGateActions.alternativeLoginRequest,
  clear: authorizationGateActions.clear,
  sendLoginRequestViaEmail: authorizationGateActions.sendLoginRequestViaEmail
};

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions;

const AuthorizationGateBase = (props: PropsWithChildren<Props>) => {
  const {
    getLoginRequest,
    renewAccessTokenRequest,
    setLoginState,
    authorizationGateState,
    loginState,
    validateUserRequest,
    validateOtpRequest,
    changePasswordRequest,
    setPasswordRequest,
    loginFlowState,
    loginUsername,
    rememberMe,
    setRememberMe,
    setForgotPassword,
    clear,
    requestId,
    setEmail,
    setLoginFlowState,
    sendLoginRequestViaEmail,
    alternativeLoginRequest
  } = props;

  const genericTranslationsT = useGenericTranslationsT();
  const loginPageT = useLoginPageT();
  const resetPasswordPageT = useResetPasswordPageT();
  const profileInfoPageT = useProfileInfoPageT();
  const newPasswordPageT = useNewPasswordPageT();
  const loginWithOtpPageT = useLoginWithOtpPageT();

  const navigate = useNavigate();
  const theme = useTheme();

  // loader for email exists api
  const [emailExistsLoading, setEmailExistsLoading] = useState(true);

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const authType = urlSearchParams.get("type");
  const emailAddress = urlSearchParams.get("emailAddress");

  useEffect(() => {
    switch (authType) {
      case CONSTANTS.AUTH_PAGE_QUERY_TYPE.LOGIN_OTP:
        setLoginFlowState({ current: "otp", otpOption: "email", forcePasswordReset: false, emailAddress: emailAddress ?? "" });
        if (hasValueAndNotEmpty(emailAddress)) sendLoginRequestViaEmail(emailAddress, requestId);
        break;
      case CONSTANTS.AUTH_PAGE_QUERY_TYPE.NEW_PASSWORD:
        setLoginFlowState({ current: "new-password", stage: "reset-password" });
        break;
      case CONSTANTS.AUTH_PAGE_QUERY_TYPE.RESET_PASSWORD:
        setLoginFlowState({ current: "forgot-password", stage: "email" });
    }
  }, [authType, emailAddress]);

  const initialFields = useMemo(
    (): Nullable<LoginFlowFields> => ({
      email: emailAddress ?? null,
      password: null,
      confirmPassword: null,
      oneTimeKey: null,
      username: null,
      fullName: null,
      mobilePhoneCountryId: null,
      mobilePhoneNo: null
    }),
    [emailAddress]
  );
  const initialFormController = useMemo(
    (): OptionalFormController<LoginFlowFields> => ({
      email: loginFlowState.current !== "otp" ? (loginFlowState.current === "forgot-password" ? { validators: [email] } : { validators: [required, email] }) : undefined,
      oneTimeKey: loginFlowState.current === "otp" ? { validators: [required] } : undefined,
      fullName: loginFlowState.current === "profile-info" ? { validators: state => (hasValue(state.fullName.value) ? [required] : []) } : undefined,
      mobilePhoneCountryId: loginFlowState.current === "profile-info" ? { validators: [required] } : undefined,
      mobilePhoneNo: loginFlowState.current === "profile-info" ? { validators: [required] } : undefined,
      password: loginFlowState.current === "login" ? { validators: [required] } : undefined,
      confirmPassword:
        loginFlowState.current === "new-password"
          ? { validators: state => (state.password.value !== state.confirmPassword.value ? [equal(genericTranslationsT.confirmPassword), required] : [required]) }
          : undefined
    }),
    [loginFlowState.current]
  );
  const initialFormInclude = useMemo(
    (): OptionalFormInclude<LoginFlowFields> => ({
      email: loginFlowState.current !== "otp",
      oneTimeKey: loginFlowState.current === "otp",
      fullName: loginFlowState.current === "profile-info",
      mobilePhoneCountryId: loginFlowState.current === "profile-info",
      mobilePhoneNo: loginFlowState.current === "profile-info",
      password: loginFlowState.current === "login",
      confirmPassword: loginFlowState.current === "new-password"
    }),
    [loginFlowState.current]
  );
  const loginForm = useForm(initialFields, initialFormController, initialFormInclude);

  const language = urlSearchParams.get("language");
  const { i18n } = useTranslation();
  const referralId = urlSearchParams.get("referralId");
  useEffect(() => {
    if (hasValue(language)) {
      urlSearchParams.delete("language");
      i18n.changeLanguage(language);
      setUrlSearchParams(urlSearchParams);
    }
  }, []);

  useEffect(() => {
    if (hasValue(referralId)) {
      if (authorizationGateState.loading === "not-started") {
        alternativeLoginRequest(referralId);
      } else if (authorizationGateState.loading === "has-loaded") {
        urlSearchParams.delete("referralId");
        setUrlSearchParams(urlSearchParams);
      }
    } else if (authorizationGateState.loading === "not-started") {
      // Check if there's a saved token and if it's renewable
      const doAsync = async () => {
        const stringifiedToken = localStorage.getItem(tokenKey);
        if (!hasValue(stringifiedToken)) {
          setLoginState({ currentState: "should login" });
          setEmailExistsLoading(false);
          return;
        }

        setRememberMe(true);
        const token: Token = JSON.parse(stringifiedToken);
        const email = localStorage.getItem(emailKey);

        try {
          setEmailExistsLoading(true);
          const emailExists = await LoginService.getApiV1LoginEmailExists(email || "");
          setEmailExistsLoading(false);
          setEmail(email || "");
          if (emailExists.forceOTPOnNextLogin === true || emailExists.forcePasswordResetOnNextLogin === true || emailExists.disabledLogin === true || emailExists.hasCustomerAccess === false) {
            setLoginState({ currentState: "should login" });
            return;
          }
        } catch {
          setEmailExistsLoading(false);
          setLoginState({ currentState: "should login" });
          return;
        }
        if (hasValue(token.refresh_token)) {
          renewAccessTokenRequest(token.refresh_token);
          setEmailExistsLoading(false);
          return;
        }

        setLoginState({ currentState: "should login" });
        setEmailExistsLoading(false);
      };

      doAsync();
    }
  }, [authorizationGateState.loading, renewAccessTokenRequest, setLoginState]);

  useEffect(() => {
    if (loginFlowState.current === "new-password") handleOnFieldChange("", loginForm.formController.password);
    if (loginFlowState.current === "forgot-password" && loginFlowState.stage === "email") handleOnFieldChange("", loginForm.formController.email);
  }, [loginFlowState.current]);

  if (authorizationGateState.loading === "has-loaded" && loginFlowState.current !== "change-password-request" && loginFlowState.current !== "password") {
    return <>{props.children}</>;
  }

  // Show loader if renewToken or login api called OR email exist api called
  if (authorizationGateState.loading === "is-loading" || emailExistsLoading) {
    return (
      <Stack sx={{ height: "100%", width: "100%", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (loginFlowState.current === "contact-dekox") {
    return (
      <Dialog open>
        <DialogTitle>{loginPageT.emailNotFound}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>{loginPageT.contactDeko}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <TooltipShortcut
            shortcut={{
              action: clear,
              shortCutKey: "b",
              modifier: "alt"
            }}
          >
            <Button variant="outlined" onClick={clear} t={genericTranslationsT} field="back" k={genericTranslationsK} />
          </TooltipShortcut>
        </DialogActions>
      </Dialog>
    );
  } else if (loginFlowState.current === "disabled-account") {
    return (
      <Dialog open>
        <DialogTitle>{loginPageT.userProfileisNotValid}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>{loginPageT.contactDeko}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <TooltipShortcut
            shortcut={{
              action: clear,
              shortCutKey: "b",
              modifier: "alt"
            }}
          >
            <Button variant="outlined" onClick={clear} t={genericTranslationsT} field="back" k={genericTranslationsK} />
          </TooltipShortcut>
        </DialogActions>
      </Dialog>
    );
  }

  const content = (): { header: string; description?: string; showCheckmark?: boolean } => {
    switch (loginFlowState.current) {
      case "otp":
        return {
          header: loginWithOtpPageT.oneTimeCode,
          description: loginWithOtpPageT.sentOneTimeCodeTo(emailAddress || loginFlowState.emailAddress || loginForm.getValues()?.email || ""),
          showCheckmark: true
        };
      case "new-password":
        if (loginFlowState.stage === "new-user") return { header: newPasswordPageT.createPassword };
        if (loginFlowState.stage === "reset-password") return { header: newPasswordPageT.createPassword };
      case "forgot-password":
        if (loginFlowState.stage === "code") {
          return {
            header: loginWithOtpPageT.oneTimeCode,
            description: loginWithOtpPageT.sentOneTimeCodeTo(emailAddress || loginForm.getValues()?.email || ""),
            showCheckmark: true
          };
        }
        return { header: resetPasswordPageT.enterEmailResetPassword };
      case "login":
        return { header: loginPageT.welcomeBack };
      case "profile-info":
        return { header: profileInfoPageT.completeYourProfile, description: profileInfoPageT.completeYourProfileHint };
      case "completion":
        if (loginFlowState.stage === "new-user") return { header: loginWithOtpPageT.yourProfileHasBeenCreated, description: loginWithOtpPageT.youCanNowLoginToProfile, showCheckmark: true };
        if (loginFlowState.stage === "password-reset-success") return { header: newPasswordPageT.newPasswordCreated, description: newPasswordPageT.newPasswordCreatedHint, showCheckmark: true };
        if (loginFlowState.stage === "reset-email-sent")
          return {
            header: resetPasswordPageT.emailSent,
            description: resetPasswordPageT.weHaveSentLinkToResetPassword(emailAddress || loginForm.getValues()?.email || ""),
            showCheckmark: true
          };
    }
    return { description: "", header: "" };
  };

  const handleBack = () => {
    loginForm.resetChanges();
    setLoginFlowState({ current: "login" });
    setLoginState({ currentState: "should login" });
    navigate("/");
  };

  return (
    <Stack flex={1} flexDirection="row" sx={{ backgroundColor: theme.palette.customColors.lightGrey }}>
      <Stack width={156} padding={3} sx={{ backgroundImage: `url(${sidebarBackground})`, backgroundPosition: "bottom", backgroundRepeat: "no-repeat", backgroundSize: "cover" }}>
        <Box
          component="img"
          src={dekoLogoAndText}
          alt="Deko logo"
          width={89}
          sx={{ cursor: "pointer" }}
          onClick={() => {
            navigate(ROUTES_URL.HOME);
          }}
        />
      </Stack>

      <Stack flex={1.2}>
        <Box component="img" src={authBackgroundImage} alt="Background image" height="100%" width="100%" sx={{ objectFit: "cover" }} />
      </Stack>

      <Stack flex={1} justifyContent="center" minWidth={500} paddingX={6} paddingY={3}>
        <Stack flex={1} justifyContent="center" width="100%">
          {loginFlowState.current !== "login" && (
            <Box mb={2} ml={-1}>
              <IconButton k={genericTranslationsK.back} onClick={handleBack}>
                <DekoxIcon icon="arrow-back" />
              </IconButton>
            </Box>
          )}
          <Stack gap={1} direction="row">
            {content().showCheckmark && <CheckCircleOutlineIcon color="primary" fontSize="large" />}
            <Stack gap={2} width="100%">
              <Typography variant="h1" color={theme.palette.customColors.darkGrey}>
                {content().header}
              </Typography>
              <Typography variant="body1" color={theme.palette.customColors.darkGrey}>
                {content().description ?? ""}
              </Typography>
              <LoginFlow
                performLogin={getLoginRequest}
                loginState={loginState}
                validateUser={validateUserRequest}
                validateOtp={validateOtpRequest}
                requestId={requestId}
                changePassword={changePasswordRequest}
                setPassword={setPasswordRequest}
                loginUsername={loginUsername ?? emailAddress ?? ""}
                rememberMe={rememberMe}
                setRememberMe={setRememberMe}
                setForgotPassword={setForgotPassword}
                back={clear}
                loginForm={loginForm}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const AuthorizationGate = connect(mapStateToProps, ownActions)(AuthorizationGateBase);
